<template>
  <dx-modal :value="show"
            title="Conferir caixa"
            size="md"
            @input="close()"
            id="modal-cash-register-review">
    <div class="review-context" v-if="closing">
      <div class="review-context-item">
        <small>Operador</small>
        <div>{{ session.user.name }}</div>
      </div>
      <div class="review-context-item">
        <small>Fechado em</small>
        <div>{{ closing.date | date('datetime') }}</div>
      </div>
      <div class="review-context-item">
        <small>Diferença no dinheiro</small>
        <div class="text-bold" :class="differenceClass">
          {{ closing.cashDifference | currency }}
        </div>
      </div>
    </div>

    <div class="review-choices">
      <label class="review-choice ok" :class="{active: form.status === 'ok'}">
        <input type="radio" value="ok" v-model="form.status" @change="$v.form.notes.$reset()">
        <span class="review-choice-icon"><fa-icon :icon="['fal', 'check']"/></span>
        <span class="review-choice-title">Sem ressalva</span>
        <span class="review-choice-hint">Os valores batem e o dinheiro chegou à tesouraria.</span>
      </label>
      <label class="review-choice flagged" :class="{active: form.status === 'flagged'}">
        <input type="radio" value="flagged" v-model="form.status">
        <span class="review-choice-icon"><fa-icon :icon="['fal', 'exclamation-triangle']"/></span>
        <span class="review-choice-title">Com ressalva</span>
        <span class="review-choice-hint">Há diferença ou pendência a resolver com o operador.</span>
      </label>
    </div>

    <div class="form-group" :class="{'has-error': $v.form.notes.$error}">
      <label class="form-label" for="review-notes">
        {{ form.status === 'flagged' ? 'Motivo da ressalva' : 'Observações' }}
        <small class="text-gray" v-if="form.status !== 'flagged'">(opcional)</small>
      </label>
      <textarea id="review-notes"
                class="form-input"
                v-model="form.notes"
                rows="3"
                :placeholder="notesPlaceholder"
                @blur="$v.form.notes.$touch()"></textarea>
      <div class="form-input-hint" v-if="$v.form.notes.$error">Campo obrigatório</div>
    </div>

    <template slot="footer">
      <button class="btn btn-icon btn-icon-left mr-1"
              :class="[
                form.status === 'flagged' ? 'btn-warning' : 'btn-primary',
                {loading: saving},
              ]"
              :disabled="saving" @click="save">
        <fa-icon :icon="['fal', 'check']"/>
        {{ form.status === 'flagged' ? 'Salvar com ressalva' : 'Confirmar conferência' }}
      </button>
      <button class="btn" @click="close()">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { requiredIf } from 'vuelidate/lib/validators';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    session: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      saving: false,
      form: {
        status: this.session.review && this.session.review.status === 'flagged' ? 'flagged' : 'ok',
        notes: this.session.review ? this.session.review.notes || '' : '',
      },
    };
  },
  computed: {
    closing() {
      return this.session.status === 'closed' ? this.session.closing : null;
    },
    differenceClass() {
      if (!this.closing || this.closing.cashDifference === 0) return 'text-success';
      return 'text-error';
    },
    notesPlaceholder() {
      return this.form.status === 'flagged'
        ? 'Ex.: faltaram R$ 20,00 no dinheiro, operador vai repor amanhã'
        : 'Alguma anotação para o histórico deste caixa';
    },
  },
  validations() {
    return {
      form: {
        notes: {
          required: requiredIf(() => this.form.status === 'flagged'),
        },
      },
    };
  },
  methods: {
    save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) return;

      this.saving = true;
      this.$http.put(`/cash-register/sessions/${this.session.id}/review`, {
        status: this.form.status,
        notes: this.form.notes.trim() || null,
      })
        .then(({ data }) => {
          this.$toast.success('Conferência registrada.');
          this.$emit('saved', data);
          this.close(true);
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    close(saved = false) {
      this.$emit('close', saved);
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
#modal-cash-register-review {
  .review-context {
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    display: flex;
    margin-bottom: $layout-spacing;
    .review-context-item {
      border-right: $border-color solid $border-width;
      flex: 1 1 0;
      padding: $layout-spacing-sm $layout-spacing;
      &:last-child {
        border-right: 0;
        text-align: right;
      }
      small {
        color: $gray-color-dark;
        display: block;
        font-size: $font-size-xs;
      }
    }
  }
  .review-choices {
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: 1fr 1fr;
    margin-bottom: $layout-spacing;
    @media screen and (max-width: $size-sm) {
      grid-template-columns: 1fr;
    }
  }
  .review-choice {
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    cursor: pointer;
    display: block;
    margin: 0;
    padding: $layout-spacing $layout-spacing $layout-spacing 3rem;
    position: relative;
    input {
      opacity: 0;
      position: absolute;
    }
    .review-choice-icon {
      align-items: center;
      background-color: $gray-color-ultra-light;
      border-radius: 50%;
      color: $gray-color-dark;
      display: flex;
      height: 1.8rem;
      justify-content: center;
      left: $layout-spacing;
      position: absolute;
      top: $layout-spacing;
      width: 1.8rem;
    }
    .review-choice-title {
      display: block;
      font-weight: 600;
    }
    .review-choice-hint {
      color: $gray-color-dark;
      display: block;
      font-size: $font-size-xs;
    }
    &:hover {
      border-color: $gray-color;
    }
    &.ok.active {
      background-color: lighten($success-color, 45%);
      border-color: $success-color;
      .review-choice-icon {
        background-color: $success-color;
        color: $light-color;
      }
      .review-choice-title {
        color: darken($success-color, 15%);
      }
    }
    &.flagged.active {
      background-color: lighten($warning-color, 40%);
      border-color: $warning-color;
      .review-choice-icon {
        background-color: $warning-color;
        color: $light-color;
      }
      .review-choice-title {
        color: darken($warning-color, 25%);
      }
    }
  }
}
</style>
